import React, { useState } from 'react';
import { Input, InputNumber, Button, Modal, Spin, Select, Radio, Checkbox } from 'antd';
import { useLeaveTypeForm } from './hooks/useLeaveTypeForm';

const numPattern = /^[0-9]*$/;

const categoryOptions = [
  { value: 1, label: '特休' },
  { value: 2, label: '一般假' },
  { value: 3, label: '非休假' },
];

const P9108Form: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    form, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
  } = useLeaveTypeForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除假別「${current.f908_01}」（${current.f908_11}）？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) {
      quickSearch(searchText.trim());
    }
  };

  const handleNumBlur = (field: string, raw: string, max?: number) => {
    const n = parseFloat(raw);
    if (isNaN(n)) {
      setField(field, 0);
    } else if (max !== undefined && n > max) {
      setField(field, max);
    } else {
      setField(field, n);
    }
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-2xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          假別設定 (pw9108f1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入代號快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 200 }}
            maxLength={1}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          {/* Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">假別代號 (F908_01)</label>
            <Input
              value={form.code}
              onChange={e => setField('code', e.target.value)}
              maxLength={1}
              disabled={mode !== 'add'}
              style={{ width: 80 }}
              placeholder="1碼"
            />
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">假別名稱 (F908_11)</label>
            <Input
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              maxLength={20}
              disabled={mode !== 'add'}
              style={{ width: 300 }}
              placeholder="最多20字"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">假別類別 (F908_10)</label>
            <Select
              value={form.category}
              onChange={v => setField('category', v)}
              disabled={!isEditing}
              style={{ width: 200 }}
              options={categoryOptions}
            />
          </div>

          {/* Calc unit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">計算單位 (F908_12)</label>
            <Radio.Group
              value={form.calUnit}
              onChange={e => setField('calUnit', e.target.value)}
              disabled={!isEditing}
            >
              <Radio value={1}>時</Radio>
              <Radio value={2}>日</Radio>
              <Radio value={3}>次</Radio>
            </Radio.Group>
          </div>

          {/* HR Review */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">是否經人事審核 (F908_17)</label>
            <Radio.Group
              value={form.hrReview}
              onChange={e => setField('hrReview', e.target.value)}
              disabled={!isEditing}
            >
              <Radio value={1}>是</Radio>
              <Radio value={2}>否</Radio>
            </Radio.Group>
          </div>

          {/* Numeric fields row 1 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">每次最低數 (F908_13)</label>
              <InputNumber
                value={form.minPerTime}
                onChange={v => setField('minPerTime', v ?? 0)}
                onBlur={e => handleNumBlur('minPerTime', e.target.value)}
                disabled={!isEditing}
                min={0}
                max={999}
                step={0.5}
                precision={1}
                style={{ width: 150 }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">每次最高數 (F908_14)</label>
              <InputNumber
                value={form.maxPerTime}
                onChange={v => setField('maxPerTime', v ?? 0)}
                onBlur={e => handleNumBlur('maxPerTime', e.target.value)}
                disabled={!isEditing}
                min={0}
                max={999999}
                step={0.5}
                precision={1}
                style={{ width: 150 }}
              />
            </div>
          </div>

          {/* Numeric fields row 2 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">每年可用數 (F908_15)</label>
              <InputNumber
                value={form.maxPerYear}
                onChange={v => setField('maxPerYear', v ?? 0)}
                onBlur={e => handleNumBlur('maxPerYear', e.target.value)}
                disabled={!isEditing}
                min={0}
                max={999999}
                step={0.5}
                precision={1}
                style={{ width: 150 }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">初核最高單位數 (F908_21)</label>
              <InputNumber
                value={form.firstApprovalMax}
                onChange={v => setField('firstApprovalMax', v ?? 0)}
                onBlur={e => handleNumBlur('firstApprovalMax', e.target.value)}
                disabled={!isEditing}
                min={0}
                max={999}
                step={0.5}
                precision={1}
                style={{ width: 150 }}
              />
            </div>
          </div>

          {/* Deduct rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">扣薪比率 % (F908_16)</label>
            <InputNumber
              value={form.deductRate}
              onChange={v => setField('deductRate', v ?? 0)}
              onBlur={e => handleNumBlur('deductRate', e.target.value, 100)}
              disabled={!isEditing}
              min={0}
              max={100}
              style={{ width: 150 }}
              addonAfter="%"
            />
          </div>

          {/* Checkboxes */}
          <div className="flex gap-6">
            <Checkbox
              checked={form.includeHolidays}
              onChange={e => setField('includeHolidays', e.target.checked)}
              disabled={!isEditing}
            >
              包含例假日 (F908_22)
            </Checkbox>
            <Checkbox
              checked={form.excludeWorkHours}
              onChange={e => setField('excludeWorkHours', e.target.checked)}
              disabled={!isEditing}
            >
              不計算上班時數 (F908_23)
            </Checkbox>
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f908_91} | 修改人員: {current.f908_93} | 日期: {current.f908_92 || '-'}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P9108Form;
